import React, { useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { c } from '../theme';
import useResponsive from '../hooks/useResponsive';
import AdminNav, { ADMIN_SIDEBAR_WIDTH, ADMIN_SIDEBAR_COLLAPSED } from './AdminNav';
import CustomerNav, { SIDEBAR_WIDTH, SIDEBAR_COLLAPSED } from './CustomerNav';
import CartFab from './CartFab';
import VendorNav from './VendorNav';
import AgentNav from './AgentNav';

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, isVendor, isAgent, signOut } = useAuth();
  const { isMobile } = useResponsive();
  const isAdminRoute = location.pathname.startsWith('/admin');
  // Only the customer nav becomes a fixed sidebar, so only customer pages
  // need clearing. Admin, vendor and agent keep their own layouts.
  const isCustomer = !isAdmin && !isVendor && !isAgent;

  // The state lives here rather than in CustomerNav because the page padding
  // has to move with it. Persisted so it survives a reload.
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem('mv_sidebar_collapsed') === '1';
    } catch (err) {
      return false;
    }
  });

  const toggleSidebar = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem('mv_sidebar_collapsed', next ? '1' : '0');
      } catch (err) {
        // Private mode blocks storage; the toggle still works for this session.
      }
      return next;
    });
  }, []);

  // Both the customer and admin navs are fixed sidebars on desktop, so the
  // page has to be cleared by whichever width is current. Vendor and agent
  // navs are unchanged and need no padding.
  let sidebarPad = 0;
  if (!isMobile && isCustomer) {
    sidebarPad = (collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH) + 'px';
  } else if (!isMobile && isAdmin) {
    sidebarPad = (collapsed ? ADMIN_SIDEBAR_COLLAPSED : ADMIN_SIDEBAR_WIDTH) + 'px';
  }

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      navigate('/login');
    }
  };

  const renderNav = () => {
    if (isAdmin) return <AdminNav collapsed={collapsed} onToggle={toggleSidebar} />;
    if (isVendor) return <VendorNav />;
    if (isAgent) return <AgentNav />;
    return <CustomerNav collapsed={collapsed} onToggle={toggleSidebar} />;
  };

  return (
    <div
      className={isAdminRoute ? 'mv-admin' : 'mv-app'}
      style={{ minHeight: '100vh', background: c.canvas }}
    >
      {renderNav()}
      <div style={{ minHeight: '100vh', paddingLeft: sidebarPad, transition: 'padding-left 0.18s ease' }}>
        {children}
      </div>
      {/* Decides for itself whether to show; it is route- and count-aware. */}
      {isCustomer && <CartFab />}
    </div>
  );
};

export default Layout;